import {
  HeaderOptions,
  Layout,
  ResourceSavingView,
  Screen,
} from '@react-navigation/elements';
import {
  CommonActions,
  createNavigatorFactory,
  DrawerRouter,
  Link,
  NavigationHelpers,
  NavigationState,
  ParamListBase,
  RouteProp,
  useNavigationBuilder,
} from '@react-navigation/native';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Dimensions,
  TouchableOpacity,
  Image,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaFrame } from 'react-native-safe-area-context';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import RestyleIcon from '@components/shared/Icon/RestyleIcon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import { Overlay } from '@components/Tutorialization/Web/OverlayWeb';
import { ChatListPanel } from '@components/Web/Chats/ChatListPanel.web';
import { ChatPanel } from '@components/Web/Chats/ChatPanel.web';
import {
  ChatDrawerScreen,
  useWebDrawer,
  WebDrawerProvider,
} from '@components/Web/Drawer/WebDrawerContext';
import { DrawerHeader } from '@components/Web/Navigator/DrawerHeader';
import { MainPanel } from '@components/Web/Navigator/MainPanel.web';
import {
  DrawerIconProps,
  resolveDrawerIcon,
} from '@components/Web/Navigator/resolveDrawerIcon';
import {
  useGetChatLazyQuery,
  useListChatsPaginationLazyQuery,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import { useChatTutorialWeb } from '@hooks/useChatTutorialWeb';
import { useDebounce } from '@hooks/useDebounce';
import useGlobalSearch from '@hooks/useGlobalSearch';
import { useHover } from '@hooks/useHover';
import { useNotification } from '@hooks/useNotification';
import useSearch from '@hooks/useSearch';
import useTabFocus from '@hooks/useTabFocus';
import {
  useWebDrawerNavigator,
  WebDrawerNavigatorProvider,
} from '@hooks/useWebDrawerNavigator';
import { useAppNavigation } from '@navigation/useAppNavigation';

type DrawerLinkViewProps = {
  routeName: string;
  active: boolean;
  drawerCollapsed: boolean;
  iconProps?: DrawerIconProps;
  drawerIcon: ReactNode;
  label: string;
  onPress: () => void;
};

/**
 * Sidebar/drawer nav item (TA-9005). The full item tints to greenSecondaryMild
 * on hover and on the active route; the label always shifts to greenSecondary
 * when active (hover keeps the neutral colour). The icon does the same only
 * for routes without an `activeName` — routes that define one swap to their
 * gradient-filled icon instead (TA-10400), via `resolveDrawerIcon`. Extracted
 * to a module-scope component so `useHover` is not called inside the
 * `renderLink` loop and its hover state survives parent re-renders.
 */
const DrawerLinkView = ({
  routeName,
  active,
  drawerCollapsed,
  iconProps,
  drawerIcon,
  label,
  onPress,
}: DrawerLinkViewProps) => {
  const { isHovered, hoverProps } = useHover();
  const resolvedIcon = resolveDrawerIcon(iconProps, active);
  return (
    <Link to={{ screen: routeName, params: {} } as any} onPress={onPress}>
      <Box
        {...hoverProps}
        width='100%'
        borderRadius='xs'
        flexDirection='row'
        justifyContent={drawerCollapsed ? 'center' : 'flex-start'}
        padding='m'
        marginBottom='xs'
        backgroundColor={active || isHovered ? 'greenSecondaryMild' : undefined}
        alignItems='center'>
        {drawerIcon ? (
          <Box style={{ marginLeft: drawerCollapsed ? 0 : -10 }} mr='xxs'>
            {drawerIcon}
          </Box>
        ) : (
          resolvedIcon.name && (
            <RestyleIcon
              {...resolvedIcon}
              width={20}
              height={20}
              marginRight={!drawerCollapsed ? 'm' : undefined}
            />
          )
        )}
        {!drawerCollapsed && (
          <Text
            variant='sidebarNavLabel'
            opacity={1}
            color={active ? 'greenSecondary' : undefined}>
            {label}
          </Text>
        )}
      </Box>
    </Link>
  );
};

/**
 * "Explore" link in the product-guides card. Inline-link rule (TA-9005):
 * no underline at rest, underline appears on hover. Module-scope so useHover
 * is not called inside the drawer's render body.
 */
const ExploreLink = () => {
  const { isHovered, hoverProps } = useHover();
  return (
    <TouchableOpacity
      onPress={() => {
        window.open('https://portal.tasktag.com/product', '_blank', 'noopener');
      }}
      accessibilityLabel='Explore'
      style={{
        width: '100%',
      }}>
      <Box flexDirection='row' {...hoverProps}>
        <Text
          variant='buttonLabel'
          color='greenSecondary'
          marginRight='xxs'
          textDecorationLine={isHovered ? 'underline' : 'none'}>
          Explore
        </Text>
        <Icon
          name='ArrowRight'
          color='greenSecondary'
          width={18}
          height={18}
          padding='xxs'
        />
      </Box>
    </TouchableOpacity>
  );
};

export type DrawerHeaderProps = {
  layout: Layout;
  options: ScreenOptions;
  route: RouteProp<ParamListBase>;
  navigation: NavigationHelpers<ParamListBase, NavDrawerEventMap>;
};

type ScreenOptions = {
  /** Props for the link icon. If no `name` is provided, no icon is displayed. */
  iconProps?: DrawerIconProps;
  /**
   * Whether the screen should be mounted immediately or wait until first navigation.
   *
   * Defaults to `true`
   */
  lazy?: boolean;
  /**
   * True if the screen should be unmounted when navigated away from.
   *
   * Defaults to `false`
   */
  unmountOnBlur?: boolean;
  /**
   * Title string of a screen displayed in the drawer.
   * When `undefined`, the scene title is used.
   */
  drawerLabel?: string;
  /** Custom component for the screen icon. */
  drawerIcon?: () => ReactNode;
  /** Title text for the screen. */
  title?: string;
  /** True if the menu item should be displayed at the bottom of the drawer
   *
   * Defaults to `false`
   */
  showBelowTheFold?: boolean;
  /**
   * Whether to show the header. Setting this to `false` hides the header.
   * Defaults to `true`.
   */
  headerShown?: boolean;
  /**
   * Whether to hide the associated menu item.
   * Used when a should exist but not be directly accessible.
   */
  hideMenuItem?: boolean;
} & Pick<HeaderOptions, 'headerTransparent'>;

export type NavDrawerEventMap = {
  drawerItemPress: { data: undefined; canPreventDefault: true };
};

const ROUTE_TAB_FOCUS: Record<string, string> = {
  home: '',
  'projects-stack': 'Projects',
  'tasks-stack': 'Tasks',
  'files-stack': 'Files',
  'contacts-stack': 'Contacts',
  'teams-stack': 'Teams',
};

const getRouteTabFocus = (routeName: string): string =>
  ROUTE_TAB_FOCUS[routeName] ?? '';

const isFocusedRoute = (routeName: string, tabFocus: string): boolean =>
  routeName in ROUTE_TAB_FOCUS && ROUTE_TAB_FOCUS[routeName] === tabFocus;

type Props = {
  initialRouteName?: string;
  screenOptions?: ScreenOptions;
  children: ReactNode;
};

const WebDrawerNavigator = ({
  initialRouteName,
  screenOptions,
  children,
}: Props) => {
  const {
    isVisibleForChat,
    showOverlayForChat,
    chatTutorialWebState,
    isFromChatList,
  } = useChatTutorialWeb();
  const { tabFocus, setTabFocus } = useTabFocus();
  const { state, navigation, descriptors, NavigationContent } =
    useNavigationBuilder(DrawerRouter, {
      children,
      initialRouteName,
      screenOptions,
    });
  const { width: currentWindowWidth } = useWindowDimensions();
  const debouncedWidth = useDebounce(currentWindowWidth, 150);
  const { setSearch, setIsSearching, setIsSearchFocus } = useGlobalSearch();
  const {
    setActiveChat,
    activeChat,
    setActiveMessageId,
    drawerCollapsed,
    setDrawerCollapsed,
    isOpenChat,
    setIsOpenChat,
  } = useActiveChat();
  const {
    chatListOpen,
    setChatListOpen,
    chatExpanded,
    setChatExpanded,
    setShouldChatListOpen,
  } = useWebDrawer();
  const { setIsSearching: setChatDetailIsSearching, setIsSearchPreview } =
    useSearch();
  const navigate = useAppNavigation();
  const [getListChats, { data }] = useListChatsPaginationLazyQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      first: 20,
    },
  });

  const [getChat, { data: chatData }] = useGetChatLazyQuery();
  const { setSelectedTeam } = useWebDrawerNavigator();
  const {
    notificationData: {
      chatId: notificationChatId,
      projectUUId: notificationProjectUUId,
    } = {},
    clearNotificationData,
  } = useNotification();

  const { chatId = '', messageId = '' } =
    (state.routes[state.index].params as {
      chatId: string;
      messageId?: string;
    }) || {};

  const notification_chat_id = window.sessionStorage.getItem(
    'notification_chat_id'
  );

  const notification_project_uuid = window.sessionStorage.getItem(
    'notification_project_uuid'
  );

  // Navigate to a project when a project-nudge push notification is clicked
  // (TA-10185). Mirrors the notificationChatId-driven chat navigation below.
  useEffect(() => {
    const projectUUId = notificationProjectUUId || notification_project_uuid;
    if (!projectUUId) return;

    clearNotificationData();
    window.sessionStorage.removeItem('notification_project_uuid');
    navigation.navigate('projects-stack', {
      screen: 'details',
      params: { projectUUId },
    });
  }, [notificationProjectUUId, notification_project_uuid]);

  useEffect(() => {
    if (
      activeChat &&
      window.sessionStorage.getItem('notification_open_chat') === 'true' &&
      !isOpenChat
    ) {
      chatExpanded && setChatExpanded(false);
      setShouldChatListOpen(true);
      setChatListOpen(false);
      setIsOpenChat(true);
      setChatDetailIsSearching(false);

      setSearch('');
      setIsSearching(false);
      setIsSearchPreview(false);
      navigate.navigateToChatDrawer({
        screen: ChatDrawerScreen.details,
        activeChat: activeChat,
      });
    }
  }, [activeChat]);

  useEffect(() => {
    if (messageId) setActiveMessageId?.(messageId);
  }, [messageId]);

  useEffect(() => {
    if (debouncedWidth <= 1440) {
      !drawerCollapsed && setDrawerCollapsed(true);
    } else {
      drawerCollapsed && setDrawerCollapsed(false);
    }
  }, [debouncedWidth]);

  useEffect(() => {
    const id = notificationChatId || chatId || notification_chat_id;

    if (id && (!activeChat?.id || id !== activeChat?.id)) {
      // Start a promise chain so we can capture `id` in a closer.
      Promise.resolve(id)
        .then(async (id) => {
          const { listChatsPagination = [] } =
            data || (await getListChats()).data || {};
          let chat = listChatsPagination.edges
            .map(({ node: chat }) => chat)
            .find(({ id: listId }) => id === listId);

          if (!chat) {
            await getChat({
              variables: {
                id,
              },
            });
            chat = chatData?.getChat;
          }
          return chat;
        })
        .then(setActiveChat);
    }
    // Since we have captured `id` already, we can safely clear it.
    navigation.setParams({ chatId: undefined });
    clearNotificationData();
    window.sessionStorage.setItem('notification_chat_id', '');
  }, [chatId, notificationChatId, data, activeChat, notification_chat_id]);

  const focusedFouteKey = state.routes[state.index].key;
  const [loaded, setLoaded] = useState([focusedFouteKey]);
  const dimensions = useSafeAreaFrame();

  if (!loaded.includes(focusedFouteKey)) {
    setLoaded([...loaded, focusedFouteKey]);
  }

  const renderLink = (route: (typeof state.routes)[0], index: number) => {
    const focused = index === state.index;
    const { options } = descriptors[route.key];
    const { iconProps, title, drawerLabel, drawerIcon } = options;
    const DrawerIconComponent = drawerIcon ? drawerIcon() : null;

    const onPress = () => {
      setSelectedTeam(undefined);
      const event = navigation.emit({
        type: 'drawerItemPress',
        target: route.key,
        canPreventDefault: true,
      });

      if (!('defaultPrevented' in event) || !event.defaultPrevented) {
        navigation.dispatch({
          ...CommonActions.reset({
            routes: [
              {
                key: route.key,
                name: route.name,
                params: route.params,
                path: route.path,
              },
            ],
            index: 0,
          }),
          target: state.key,
        });

        setIsSearching(false);
      }

      setTabFocus(getRouteTabFocus(route.name));

      setIsSearchFocus(false);
      setSearch('');
    };

    const isFocused =
      state.index === 5 ? isFocusedRoute(route.name, tabFocus) : false;

    return (
      <DrawerLinkView
        key={route.key}
        routeName={route.name}
        active={focused || isFocused}
        drawerCollapsed={drawerCollapsed}
        iconProps={iconProps}
        drawerIcon={DrawerIconComponent}
        label={drawerLabel || title || route.name}
        onPress={onPress}
      />
    );
  };

  const isMobileBrowser = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      window.navigator.userAgent
    );
  }, []);

  const chatWidth = Dimensions.get('window').width <= 1280 ? 480 : 550;
  const width = `${isOpenChat || chatListOpen ? chatWidth - 80 : 0}px`;

  const showChatTutorialOverlay =
    isVisibleForChat &&
    showOverlayForChat &&
    chatTutorialWebState !== '3' &&
    chatTutorialWebState !== (isFromChatList ? '9' : '8') &&
    !(chatTutorialWebState === '4' && isFromChatList);

  const showChatListTutorialOverlay =
    isVisibleForChat &&
    showOverlayForChat &&
    chatTutorialWebState === '4' &&
    isFromChatList;

  return (
    <NavigationContent>
      <WebDrawerProvider
        screen={(activeChat && ChatDrawerScreen.details) || undefined}>
        <WebDrawerNavigatorProvider>
          <Box
            flexDirection='row'
            flex={1}
            flexWrap='nowrap'
            style={{ overflow: 'hidden' }}>
            {!isMobileBrowser && (
              <Box
                padding={drawerCollapsed ? 's' : 'l'}
                py={drawerCollapsed ? 'l' : undefined}
                backgroundColor='grey01'
                width={drawerCollapsed ? 86 : 256}>
                <Box
                  flexDirection={drawerCollapsed ? 'column' : 'row'}
                  justifyContent={
                    drawerCollapsed ? 'flex-start' : 'space-between'
                  }
                  alignItems={drawerCollapsed ? 'center' : undefined}>
                  <Box
                    flex={drawerCollapsed ? undefined : 1}
                    alignItems='center'
                    justifyContent='flex-start'>
                    <Box alignSelf={drawerCollapsed ? 'center' : 'flex-start'}>
                      <Icon
                        name={drawerCollapsed ? 'LogoInitials' : 'Logo'}
                        width={drawerCollapsed ? 25 : 80}
                        height={24}
                        color='textPrimary'
                      />
                    </Box>
                  </Box>
                  <Box
                    flex={drawerCollapsed ? undefined : 1}
                    alignItems='center'
                    justifyContent={drawerCollapsed ? 'center' : 'flex-end'}
                    marginTop={drawerCollapsed ? 'l' : undefined}>
                    <Box alignSelf={drawerCollapsed ? 'center' : 'flex-end'}>
                      <WebIcon
                        accessibilityLabel={
                          drawerCollapsed ? 'Expand menu' : 'Collapse menu'
                        }
                        name={
                          drawerCollapsed ? 'ChevronsRight' : 'ChevronsLeft'
                        }
                        variant='l'
                        onPress={() => setDrawerCollapsed((e) => !e)}
                        color='textPrimary'
                        // Rail bg is grey01 — hover one step deeper.
                        hoverBackgroundColor='grey02'
                      />
                    </Box>
                  </Box>
                </Box>
                <Box
                  flexDirection='column'
                  justifyContent='space-between'
                  alignContent='center'
                  flex={1}
                  my='l'>
                  <Box flexDirection='column' alignContent='center'>
                    {state.routes.map((route, index) => {
                      const { options = {} } = descriptors[route.key];
                      const { showBelowTheFold = false, hideMenuItem = false } =
                        options;
                      return (
                        (!hideMenuItem &&
                          !showBelowTheFold &&
                          renderLink(route, index)) ||
                        null
                      );
                    })}
                  </Box>
                  <Box flexDirection='column' alignSelf='flex-end' width='100%'>
                    {!drawerCollapsed && (
                      <Box
                        flexDirection='column'
                        alignContent='center'
                        width='100%'
                        borderRadius='xs'
                        padding='m'
                        marginBottom='xl'
                        backgroundColor='white'
                        style={{
                          position: 'relative',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                        }}>
                        <Text
                          variant='labelLarge'
                          color='textSecondary'
                          marginBottom='m'>
                          Find All Product Guides Here
                        </Text>
                        <ExploreLink />
                        <Box
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            zIndex: -1,
                          }}>
                          <Image
                            source={require('../../../../assets/images/BlobTop.png')}
                            style={{
                              width: 56,
                              height: 56,
                            }}
                          />
                        </Box>
                        <Box
                          style={{
                            position: 'absolute',
                            right: 0,
                            bottom: 0,
                            zIndex: -1,
                          }}>
                          <Image
                            source={require('../../../../assets/images/BlobBottom.png')}
                            style={{
                              width: 80,
                              height: 64,
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                    <Box
                      width='100%'
                      borderColor='grey02'
                      borderTopWidth={1}
                      marginTop={drawerCollapsed ? 'xs' : 'xxs'}
                      marginBottom='xs'
                    />
                    <Box flexDirection='column' alignContent='center'>
                      {state.routes.map((route, index) => {
                        const { options = {} } = descriptors[route.key];
                        const {
                          showBelowTheFold = false,
                          hideMenuItem = false,
                        } = options;
                        return (
                          (!hideMenuItem &&
                            showBelowTheFold &&
                            renderLink(route, index)) ||
                          null
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            <MainPanel>
              {state.routes.map((route, index) => {
                const descriptor = descriptors[route.key];
                const {
                  lazy = true,
                  unmountOnBlur = false,
                  headerShown,
                } = descriptor.options;
                const isFocused = index === state.index;

                if (unmountOnBlur && !isFocused) {
                  return null;
                }

                if (lazy && !loaded.includes(route.key) && !isFocused) {
                  return null;
                }

                return (
                  <ResourceSavingView visible={isFocused} key={route.key}>
                    <Screen
                      focused={isFocused}
                      navigation={descriptor.navigation}
                      route={descriptor.route}
                      header={
                        <DrawerHeader
                          {...{
                            layout: dimensions,
                            route: descriptor.route,
                            navigation:
                              descriptor.navigation as unknown as NavigationHelpers<
                                ParamListBase,
                                NavDrawerEventMap
                              >,
                            options: descriptor.options,
                          }}
                        />
                      }
                      headerShown={isMobileBrowser ? false : headerShown}>
                      <Box
                        flex={1}
                        flexDirection='row'
                        justifyContent='space-between'>
                        <Box flex={1}>{descriptor.render()}</Box>
                      </Box>
                    </Screen>
                  </ResourceSavingView>
                );
              })}
            </MainPanel>
            {showChatTutorialOverlay ? (
              <>
                <Box
                  flex={chatExpanded ? 1 : undefined}
                  alignSelf='flex-end'
                  flexDirection='row'
                  width={chatExpanded ? undefined : width}
                  style={{
                    marginRight: chatExpanded && chatListOpen ? chatWidth : 80,
                  }}></Box>
                <Overlay
                  visible={showChatTutorialOverlay}
                  children={<ChatPanel />}
                />
              </>
            ) : (
              !isMobileBrowser && <ChatPanel />
            )}
            {!isMobileBrowser &&
              (showChatListTutorialOverlay ? (
                <Overlay
                  visible={showChatListTutorialOverlay}
                  children={<ChatListPanel />}
                />
              ) : (
                <ChatListPanel />
              ))}
          </Box>
        </WebDrawerNavigatorProvider>
      </WebDrawerProvider>
    </NavigationContent>
  );
};

export const createWebDrawerNavigator = createNavigatorFactory<
  NavigationState,
  ScreenOptions,
  NavDrawerEventMap,
  typeof WebDrawerNavigator
>(WebDrawerNavigator);
